import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Image,
  Input,
  List,
  Modal,
  Popconfirm,
  Pagination,
  Progress,
  Row,
  Slider,
  Skeleton,
  Space,
  Timeline,
  Typography,
  Upload,
  message,
  Empty,
  Steps,
  Tag,
} from 'antd';
import { CameraOutlined, CloudUploadOutlined, PictureOutlined } from '@ant-design/icons';
import ModuleNav from './ModuleNav';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import {
  listSwatchesV2,
  createTryOnJob,
  listTryOnJobs,
  getTryOnJobStatus,
  createBatchTryOnJob,
  getBatchTryOnJobStatus,
  listBatchTryOnJobs,
  listGallery,
  deleteGalleryEntry,
  deleteGalleryForUser,
} from '../../services/aiImageMaskingApi';

const { Text, Paragraph, Title } = Typography;
const { TextArea, Search } = Input;
const SWATCH_PAGE_SIZE = 8;
const PROCESSING_GIF = 'https://media.giphy.com/media/3oEjI6SIIHBdRxXI40/giphy.gif';
const PREVIEW_CARD_MIN_HEIGHT = 460;
const SELECTED_SWATCH_KEY_BASE = 'ai_tryout_selected_swatch';

const TryBeforeYouBuyV2 = () => {
  const { account } = useAuth();
  const tenantId = account?.id != null ? String(account.id) : 'global';
  const selectedSwatchStorageKey = `${SELECTED_SWATCH_KEY_BASE}:${tenantId}`;
  const [swatches, setSwatches] = useState([]);
  const [swatchSearch, setSwatchSearch] = useState('');
  const [swatchPage, setSwatchPage] = useState(1);
  const [swatchTotal, setSwatchTotal] = useState(0);
  const [swatchLoading, setSwatchLoading] = useState(false);
  const [selectedSwatch, setSelectedSwatch] = useState(null);
  const [selectedSwatches, setSelectedSwatches] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [preselectedSwatch, setPreselectedSwatch] = useState(() => {
    try {
      const cached = localStorage.getItem(selectedSwatchStorageKey) || localStorage.getItem(SELECTED_SWATCH_KEY_BASE);
      const parsed = cached ? JSON.parse(cached) : null;
      return parsed?.id || null;
    } catch (err) {
      return null;
    }
  });
  const [, setPreselectedSwatchPayload] = useState(() => {
    try {
      const cached = localStorage.getItem(selectedSwatchStorageKey) || localStorage.getItem(SELECTED_SWATCH_KEY_BASE);
      const parsed = cached ? JSON.parse(cached) : null;
      return parsed || null;
    } catch (err) {
      return null;
    }
  });
  useEffect(() => {
    try {
      const cached = localStorage.getItem(selectedSwatchStorageKey) || localStorage.getItem(SELECTED_SWATCH_KEY_BASE);
      const parsed = cached ? JSON.parse(cached) : null;
      setPreselectedSwatch(parsed?.id || null);
      setPreselectedSwatchPayload(parsed || null);
    } catch (err) {
      setPreselectedSwatch(null);
      setPreselectedSwatchPayload(null);
    }
  }, [selectedSwatchStorageKey]);
  const [preselectApplied, setPreselectApplied] = useState(false);
  const selectedSwatchData = useMemo(
    () => swatches.find((s) => s.id === selectedSwatch) || null,
    [swatches, selectedSwatch],
  );
  const primarySwatchId = useMemo(
    () => selectedSwatch || (selectedSwatches.length ? selectedSwatches[0] : null),
    [selectedSwatch, selectedSwatches],
  );
  const primarySwatchData = useMemo(
    () => selectedSwatchData || swatches.find((s) => s.id === primarySwatchId) || null,
    [primarySwatchId, selectedSwatchData, swatches],
  );
  const comparisonSwatchData = useMemo(
    () =>
      selectedSwatches
        .map((id) => swatches.find((s) => s.id === id))
        .filter(Boolean),
    [selectedSwatches, swatches],
  );
  const resolveSwatchLabel = useCallback(
    (swatchId, fallback = null) => {
      const found = swatches.find((s) => s.id === swatchId);
      return found?.name || fallback || `Swatch #${swatchId}`;
    },
    [swatches],
  );
  const resolveSwatchMeta = useCallback(
    (swatchId, fallbackName = null) => {
      const found = swatches.find((s) => s.id === swatchId);
      if (found) {
        return {
          name: found.name,
          description: found.description,
          blob_url: found.blob_url,
        };
      }
      return {
        name: fallbackName || `Swatch #${swatchId}`,
        description: '',
        blob_url: null,
      };
    },
    [swatches],
  );
  const ensureAbsoluteUrl = useCallback((url) => {
    if (!url) return url;
    // Already absolute
    if (/^https?:\/\//i.test(url)) return url;
    // Base64/data URLs should remain untouched
    if (/^data:/i.test(url)) return url;
    // Handle storage path prefixes
    if (url.startsWith('local:')) {
      const relative = url.slice('local:'.length);
      const normalized = relative.startsWith('/') ? relative : `/${relative}`;
      const base = (API_BASE_URL || window.location.origin || '').replace(/\/+$/, '');
      return `${base}/uploads_v4${normalized.replace(/^\/?/, '/')}`;
    }
    // Ensure leading slash
    const path = url.startsWith('/') ? url : `/${url}`;
    const base = (API_BASE_URL || window.location.origin || '').replace(/\/+$/, '');
    return `${base}${path}`;
  }, []);
  const clearPreselectedSwatch = useCallback(() => {
    setPreselectedSwatch(null);
    setPreselectedSwatchPayload(null);
    try {
      localStorage.removeItem(selectedSwatchStorageKey);
      localStorage.removeItem(SELECTED_SWATCH_KEY_BASE);
    } catch (err) {
      // ignore
    }
  }, [selectedSwatchStorageKey]);

  const formatSize = useCallback((bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  const [environmentFile, setEnvironmentFile] = useState([]);
  const [environmentPreview, setEnvironmentPreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [userId, setUserId] = useState('');
  const [agentTag, setAgentTag] = useState('');
  const [existingModalVisible, setExistingModalVisible] = useState(false);
  const [existingTryOn, setExistingTryOn] = useState(null);
  const [reapplyInstructions, setReapplyInstructions] = useState('');
  const [reapplyLoading, setReapplyLoading] = useState(false);

  const [jobs, setJobs] = useState([]);
  const jobStatusRef = useRef({});
  const [batchJobs, setBatchJobs] = useState([]);
  const batchJobStatusRef = useRef({});
  const latestDisplayedJobRef = useRef(0);
  const [resultPreview, setResultPreview] = useState(null);
  const [jobMeta, setJobMeta] = useState(null);
  const [debugVisible, setDebugVisible] = useState(false);
  const [debugPayload, setDebugPayload] = useState(null);
  const [missingPhotoModal, setMissingPhotoModal] = useState(false);
  const [pendingSwatchId, setPendingSwatchId] = useState(null);
  const [photoMeta, setPhotoMeta] = useState(null);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [comparisonDrawerVisible, setComparisonDrawerVisible] = useState(false);
  const [comparisonDrawerWidth, setComparisonDrawerWidth] = useState(50);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const formatDuration = useCallback((durationMs) => {
    if (!durationMs && durationMs !== 0) return null;
    const totalSeconds = durationMs / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds - minutes * 60;
    return `${minutes}m ${seconds.toFixed(1)}s`;
  }, []);
  const normalizeDebugPayload = useCallback((payload) => {
    if (!payload) {
      return null;
    }
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch (error) {
        console.warn('Unable to parse debug payload', error);
        return null;
      }
    }
    return payload;
  }, []);
  const extractJobPreview = useCallback(
    (job) => {
      const debug = normalizeDebugPayload(job?.debug_payload);
      return ensureAbsoluteUrl(job?.overlay_url) || debug?.preview_data_url || null;
    },
    [ensureAbsoluteUrl, normalizeDebugPayload],
  );
  const extractJobDuration = useCallback(
    (job) => {
      const debug = normalizeDebugPayload(job?.debug_payload);
      return job?.duration_ms ?? debug?.duration_ms ?? null;
    },
    [normalizeDebugPayload],
  );
  const photoInfo = useMemo(() => {
    const file = environmentFile?.[0];
    const sizeBytes = photoMeta?.sizeBytes ?? file?.size;
    return {
      name: photoMeta?.name || file?.name || '—',
      sizeLabel: sizeBytes !== undefined ? formatSize(sizeBytes) : '—',
      type: photoMeta?.type || file?.type || '—',
      dimensions:
        photoMeta?.width && photoMeta?.height
          ? `${photoMeta.width} × ${photoMeta.height}`
          : file
          ? 'Loading...'
          : '—',
      dpi: photoMeta?.dpi || 'N/A (browser does not expose DPI)',
    };
  }, [environmentFile, formatSize, photoMeta]);
  const normalizeBatchResults = useCallback((rawResults) => {
    if (!rawResults) return [];
    if (typeof rawResults === 'string') {
      try {
        const parsed = JSON.parse(rawResults);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    }
    return Array.isArray(rawResults) ? rawResults : [];
  }, []);
  const normalizeBatchResultsWithUrls = useCallback(
    (rawResults) =>
      normalizeBatchResults(rawResults).map((item) => ({
        ...item,
        overlay_url: ensureAbsoluteUrl(item.overlay_url),
        gallery_entry: item.gallery_entry
          ? {
              ...item.gallery_entry,
              overlay_url: ensureAbsoluteUrl(item.gallery_entry.overlay_url),
              original_url: ensureAbsoluteUrl(item.gallery_entry.original_url),
            }
          : item.gallery_entry,
      })),
    [normalizeBatchResults, ensureAbsoluteUrl],
  );
  const displayComparisonResults = useMemo(
    () => normalizeBatchResultsWithUrls(comparisonResults),
    [comparisonResults, normalizeBatchResultsWithUrls],
  );
  const normalizedUserId = userId.trim();
  const debugDetails = debugPayload && (debugPayload.debug || debugPayload);
  const requestDetails =
    debugPayload?.request ||
    debugPayload?.debug?.request ||
    debugDetails?.request ||
    ((debugPayload?.method || debugPayload?.url || debugPayload?.headers || debugPayload?.fields || debugPayload?.files) ? debugPayload : null) ||
    null;
  const responseDetails =
    debugPayload?.response ||
    debugPayload?.debug?.response ||
    debugDetails?.response ||
    requestDetails?.response ||
    null;
  const hasDebugTransportData = Boolean(requestDetails || responseDetails);
  const requestMethod = requestDetails?.method || (requestDetails?.sdk ? 'SDK call' : null) || 'N/A';
  const requestUrl =
    requestDetails?.url || (requestDetails?.sdk ? 'Google GenAI SDK (no direct HTTP URL)' : null) || 'N/A';
  const requestHeaders =
    requestDetails?.headers || (requestDetails?.sdk ? { note: 'Headers are managed internally by the SDK' } : {});
  const requestFields =
    requestDetails?.body ||
    requestDetails?.fields ||
    requestDetails?.payload ||
    (requestDetails?.sdk
      ? {
          sdk: true,
          model: requestDetails?.model || debugPayload?.model || null,
          generation_config: requestDetails?.generation_config || null,
          prompt_preview: requestDetails?.prompt || null,
        }
      : {});
  const requestFiles = requestDetails?.files || requestDetails?.parts || [];
  const failedAttempts =
    debugPayload?.debug?.failed_attempts ||
    debugPayload?.failed_attempts ||
    debugDetails?.failed_attempts ||
    [];
  const debugError =
    debugPayload?.error ||
    debugDetails?.error ||
    responseDetails?.error ||
    responseDetails?.json?.error?.message ||
    responseDetails?.json?.error ||
    null;
  const debugErrorType = responseDetails?.error_type || responseDetails?.json?.error?.type || null;
  const debugSnapshots = useMemo(() => {
    const items = [];
    const seen = new Set();
    const add = (label, url) => {
      if (!url) return;
      const normalized = ensureAbsoluteUrl(url);
      if (!normalized) return;
      const key = `${label}-${normalized}`;
      if (seen.has(key)) return;
      seen.add(key);
      items.push({ key, label, url: normalized });
    };

    // Always include the inputs for immediate visibility
    if (environmentPreview) {
      add('Lifestyle input', environmentPreview);
    }
    selectedSwatches.forEach((id, idx) => {
      const meta = resolveSwatchMeta(id, resolveSwatchLabel(id));
      const label = idx === 0 ? 'Primary swatch input' : `Swatch input ${idx + 1}`;
      add(label, meta?.blob_url);
    });

    const debug = debugDetails || {};
    const nestedDebug = debug.debug || {};

    const stageSources =
      debug.processing_stages || debug.stages || nestedDebug.processing_stages || nestedDebug.stages;
    if (Array.isArray(stageSources)) {
      stageSources.forEach((stage, idx) => {
        const label = stage?.label || stage?.name || `Stage ${idx + 1}`;
        const url =
          stage?.image_url ||
          stage?.url ||
          stage?.preview_url ||
          stage?.overlay_url ||
          stage?.preview;
        add(label, url);
      });
    }

    const imageKeys = [
      'preview_data_url',
      'overlay_url',
      'result_url',
      'output_url',
      'composited_image_url',
      'mask_url',
      'segmentation_overlay_url',
      'debug_preview_url',
      'input_url',
      'original_url',
      'lifestyle_url',
      'environment_url',
      'swatch_url',
    ];
    imageKeys.forEach((key) => {
      const url = debug[key] || nestedDebug[key] || debugPayload?.[key];
      const label = key.replace(/_/g, ' ');
      add(label, url);
    });

    return items;
  }, [
    debugDetails,
    debugPayload,
    ensureAbsoluteUrl,
    environmentPreview,
    resolveSwatchLabel,
    resolveSwatchMeta,
    selectedSwatches,
  ]);
  const activeJobs = jobs.filter((job) => ['queued', 'running'].includes(job.status));
  const showProcessing = activeJobs.length > 0;
  const currentProgress = showProcessing
    ? Math.min(
        99,
        Math.max(
          ...activeJobs.map((job) => {
            if (typeof job.progress === 'number') {
              return job.progress;
            }
            return 5;
          }),
        ),
      )
    : 0;
  const normalizeJobResponse = useCallback(
    (job) => ({
      ...job,
      job_id: job.job_id || job.jobId,
      progress: job.progress ?? (job.status === 'completed' ? 100 : 0),
      overlay_url: ensureAbsoluteUrl(job.overlay_url),
    }),
    [ensureAbsoluteUrl],
  );
  const normalizeBatchJobResponse = useCallback(
    (job) => ({
      ...job,
      job_id: job.job_id || job.jobId,
      progress: job.progress ?? (job.status === 'completed' ? 100 : 0),
      results: normalizeBatchResultsWithUrls(job.results),
    }),
    [normalizeBatchResultsWithUrls],
  );

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    const loadSwatches = async () => {
      setSwatchLoading(true);
      try {
        const data = await listSwatchesV2({
          page: swatchPage,
          page_size: SWATCH_PAGE_SIZE,
          search: swatchSearch || undefined,
        });
        setSwatches(Array.isArray(data?.items) ? data.items : []);
        setSwatchTotal(data?.total || 0);
      } catch (error) {
        message.error(error.message || 'Unable to fetch swatches');
      } finally {
        setSwatchLoading(false);
      }
    };
    loadSwatches();
  }, [swatchPage, swatchSearch]);

  // Highlight a swatch if it was selected from the Swatches page (one-time)
  useEffect(() => {
    if (preselectApplied) {
      return;
    }
    if (!swatches.length) {
      return;
    }
    setPreselectApplied(true);
    if (!preselectedSwatch) {
      return;
    }
    const found = swatches.find((s) => s.id === preselectedSwatch);
    if (found) {
      setSelectedSwatch(preselectedSwatch);
      message.info(`Swatch "${found.name}" is pre-selected from Swatches.`);
      // Clear stored breadcrumb now that it is applied
      clearPreselectedSwatch();
    }
  }, [clearPreselectedSwatch, preselectApplied, preselectedSwatch, swatches]);

  useEffect(() => {
    if (!normalizedUserId) {
      setJobs([]);
      return;
    }
    const fetchJobs = async () => {
      try {
        const data = await listTryOnJobs({ user_id: normalizedUserId, limit: 15 });
        const normalized = Array.isArray(data?.items)
          ? data.items.map((item) => ({
              ...normalizeJobResponse(item),
              overlay_url: ensureAbsoluteUrl(item.overlay_url),
            }))
          : [];
        setJobs(normalized);
      } catch (error) {
        message.error(error.message || 'Unable to load tryout jobs');
      }
    };
    fetchJobs();
  }, [normalizedUserId, normalizeJobResponse, ensureAbsoluteUrl]);

  useEffect(() => {
    if (!normalizedUserId) {
      setGalleryItems([]);
      return;
    }
    const loadGallery = async () => {
      setGalleryLoading(true);
      try {
        const data = await listGallery({ user_id: normalizedUserId, page_size: 50 });
        const items = Array.isArray(data?.items) ? data.items : [];
        setGalleryItems(
          items.map((item) => ({
            ...item,
            overlay_url: ensureAbsoluteUrl(item.overlay_url),
            original_url: ensureAbsoluteUrl(item.original_url),
          })),
        );
      } catch (error) {
        message.error(error.message || 'Unable to load user images');
      } finally {
        setGalleryLoading(false);
      }
    };
    loadGallery();
  }, [normalizedUserId, ensureAbsoluteUrl]);

  useEffect(() => {
    if (!normalizedUserId) {
      setBatchJobs([]);
      return;
    }
    const fetchBatchJobs = async () => {
      try {
        const data = await listBatchTryOnJobs({ user_id: normalizedUserId, limit: 10 });
        const normalized = Array.isArray(data?.items)
          ? data.items.map((item) => normalizeBatchJobResponse(item))
          : [];
        setBatchJobs(normalized);
      } catch (error) {
        message.error(error.message || 'Unable to load comparison jobs');
      }
    };
    fetchBatchJobs();
  }, [normalizedUserId, normalizeBatchJobResponse]);

  useEffect(() => {
    const activeJobs = jobs.filter((job) => ['queued', 'running'].includes(job.status));
    if (!activeJobs.length) {
      return;
    }
    const interval = setInterval(async () => {
      try {
        const updates = await Promise.all(
          activeJobs.map((job) =>
            getTryOnJobStatus(job.job_id)
              .then((data) => normalizeJobResponse(data))
              .catch(() => null)
          )
        );
        setJobs((prev) =>
          prev.map((job) => {
            const update = updates.find((item) => item && item.job_id === job.job_id);
            return update ? update : job;
          })
        );
      } catch (error) {
        // swallow polling errors
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [jobs, normalizeJobResponse]);

  useEffect(() => {
    const activeBatch = batchJobs.filter((job) => ['queued', 'running'].includes(job.status));
    if (!activeBatch.length) {
      return;
    }
    const interval = setInterval(async () => {
      try {
        const updates = await Promise.all(
          activeBatch.map((job) =>
            getBatchTryOnJobStatus(job.job_id)
              .then((data) => normalizeBatchJobResponse(data))
              .catch(() => null)
          )
        );
        setBatchJobs((prev) =>
          prev.map((job) => {
            const update = updates.find((item) => item && item.job_id === job.job_id);
            return update ? update : job;
          })
        );
      } catch (error) {
        // swallow polling errors
      }
    }, 4500);
    return () => clearInterval(interval);
  }, [batchJobs, normalizeBatchJobResponse]);

  useEffect(() => {
    jobs.forEach((job) => {
      const previous = jobStatusRef.current[job.job_id];
      if (previous === job.status) {
        return;
      }
      jobStatusRef.current[job.job_id] = job.status;
      if (job.status === 'completed') {
        const createdAtTs = job.created_at ? new Date(job.created_at).getTime() : Date.now();
        if (createdAtTs >= latestDisplayedJobRef.current) {
          latestDisplayedJobRef.current = createdAtTs;
          const preview = extractJobPreview(job);
          if (preview) {
            setResultPreview(preview);
          } else {
            loadLatestGalleryPreview();
          }
          const parsedDebug = normalizeDebugPayload(job.debug_payload);
          setJobMeta({
            job_id: job.job_id,
            status: job.status,
            agent_tag: job.agent_tag || 'default',
            model: parsedDebug?.model,
            duration_ms: extractJobDuration(job),
          });
          setDebugPayload(parsedDebug);
          message.success('Tryout image is ready');
        }
      } else if (job.status === 'failed') {
        message.error(job.message || 'Tryout failed');
      } else if (job.status === 'conflict') {
        message.info(job.message || 'Existing try-on found');
        if (job.overlay_url) {
          setResultPreview(job.overlay_url);
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractJobDuration, extractJobPreview, jobs, normalizeDebugPayload]);

  useEffect(() => {
    batchJobs.forEach((job) => {
      const previous = batchJobStatusRef.current[job.job_id];
      if (previous === job.status) {
        return;
      }
      batchJobStatusRef.current[job.job_id] = job.status;
      if (['completed', 'completed_with_errors'].includes(job.status)) {
        const parsedResults = normalizeBatchResults(job.results);
        setComparisonResults(parsedResults);
        setComparisonDrawerVisible(true);
        message.success('Comparison is ready. Opening preview.');
      } else if (job.status === 'failed') {
        message.error(job.message || 'Comparison job failed');
      }
    });
  }, [batchJobs, normalizeBatchResults]);

  const applyEnvironmentFile = (file) => {
    if (!file) return;
    if (environmentPreview) {
      URL.revokeObjectURL(environmentPreview);
    }
    const previewUrl = URL.createObjectURL(file);
    file.preview = previewUrl;
    setEnvironmentPreview(previewUrl);
    setEnvironmentFile([file]);
    // Reset live preview/status when a new image is uploaded
    setResultPreview(null);
    setJobMeta(null);
    setDebugPayload(null);

    // Set immediate metadata from file object (size/type/name); refine with dimensions on load
    const sizeBytes = file.size || 0;
    const type = file.type || 'Unknown';
    setPhotoMeta({
      width: null,
      height: null,
      sizeBytes,
      type,
      dpi: 'N/A (browser does not expose DPI)',
      name: file.name,
    });

    // Gather basic metadata (size/type) and dimensions via Image
    const img = new Image();
    img.onload = () => {
      setPhotoMeta({
        width: img.naturalWidth,
        height: img.naturalHeight,
        sizeBytes,
        type,
        dpi: 'N/A (browser does not expose DPI)',
        name: file.name,
      });
    };
    img.onerror = () => {
      setPhotoMeta({
        width: null,
        height: null,
        sizeBytes,
        type,
        dpi: 'N/A (browser does not expose DPI)',
        name: file.name,
      });
    };
    img.src = previewUrl;
  };

  const uploadProps = {
    multiple: false,
    fileList: environmentFile,
    beforeUpload: (file) => {
      applyEnvironmentFile(file);
      return false;
    },
    onRemove: () => {
      if (environmentPreview) {
        URL.revokeObjectURL(environmentPreview);
      }
      setEnvironmentPreview(null);
      setEnvironmentFile([]);
      setPhotoMeta(null);
    },
    accept: '.png,.jpg,.jpeg,.webp',
  };

  useEffect(() => {
    jobStatusRef.current = {};
    batchJobStatusRef.current = {};
    latestDisplayedJobRef.current = 0;
  }, [normalizedUserId]);

  const queueTryOnJob = useCallback(
    async (swatchOverride, options = {}) => {
      const { forceOverwrite = false, additionalInstructions } = options;
      const targetSwatchId = swatchOverride || selectedSwatch;
      if (!environmentFile.length) {
        message.warning('Upload a lifestyle image to continue');
        return null;
      }
      if (!targetSwatchId) {
        message.warning('Select a swatch to continue');
        return null;
      }
      if (!normalizedUserId) {
        message.warning('Enter a user ID to continue');
        return null;
      }
      try {
        const response = await createTryOnJob({
          environmentFile: environmentFile[0],
          swatchId: targetSwatchId,
          prompt,
          userId: normalizedUserId,
          agentTag: agentTag?.trim() || undefined,
          forceOverwrite,
          additionalInstructions,
        });
        const normalized = normalizeJobResponse({
          ...response,
          swatch_id: targetSwatchId,
          agent_tag: agentTag?.trim() || null,
        });
        setJobs((prev) => [normalized, ...prev.filter((job) => job.job_id !== normalized.job_id)]);
        message.success(forceOverwrite ? 'Reapply queued' : 'Tryout queued');
        return normalized;
      } catch (error) {
        if (error.code === 'tryon_exists') {
          setExistingTryOn({
            swatchId: targetSwatchId,
            ...(error.payload?.existing || {}),
          });
          setReapplyInstructions('');
          setExistingModalVisible(true);
          return null;
        }
        message.error(error.message || 'Unable to queue try-on');
        return null;
      }
    },
    [agentTag, environmentFile, normalizeJobResponse, normalizedUserId, prompt, selectedSwatch],
  );

  const queueComparisonJob = useCallback(async () => {
    if (!environmentFile.length) {
      message.warning('Upload a lifestyle image to continue');
      return null;
    }
    if (selectedSwatches.length < 2) {
      message.warning('Select at least two swatches to compare');
      return null;
    }
    if (!normalizedUserId) {
      message.warning('Enter a user ID to continue');
      return null;
    }
    try {
      const response = await createBatchTryOnJob({
        environmentFile: environmentFile[0],
        swatchIds: selectedSwatches,
        prompt,
        userId: normalizedUserId,
        agentTag: agentTag?.trim() || undefined,
        additionalInstructions: undefined,
      });
      const normalized = normalizeBatchJobResponse({
        ...response,
        swatch_ids: selectedSwatches,
        agent_tag: agentTag?.trim() || null,
      });
      setBatchJobs((prev) => [normalized, ...prev.filter((job) => job.job_id !== normalized.job_id)]);
      message.success('Comparison queued');
      return normalized;
    } catch (error) {
      message.error(error.message || 'Unable to queue comparison');
      return null;
    }
  }, [agentTag, environmentFile, normalizeBatchJobResponse, normalizedUserId, prompt, selectedSwatches]);

  const handleExistingModalClose = useCallback(() => {
    setExistingModalVisible(false);
    setExistingTryOn(null);
    setReapplyInstructions('');
    setReapplyLoading(false);
  }, []);

  const handleUseExistingResult = useCallback(() => {
    if (existingTryOn?.overlay_url) {
      setResultPreview(existingTryOn.overlay_url);
      setJobMeta({
        job_id: 'existing',
        status: 'existing',
        agent_tag: existingTryOn?.agent_tag || agentTag?.trim() || 'default',
      });
      message.success('Showing existing tryout result.');
    } else {
      message.info('Existing tryout result is not available yet.');
    }
    handleExistingModalClose();
  }, [agentTag, existingTryOn, handleExistingModalClose]);

  const handleReapplyWithInstructions = useCallback(async () => {
    if (!existingTryOn) {
      return;
    }
    setReapplyLoading(true);
    const swatchIdForReapply = existingTryOn.swatch_id || existingTryOn.swatchId;
    try {
      const job = await queueTryOnJob(swatchIdForReapply, {
        forceOverwrite: true,
        additionalInstructions: reapplyInstructions.trim() || undefined,
      });
      if (job) {
        handleExistingModalClose();
      }
    } finally {
      setReapplyLoading(false);
    }
  }, [existingTryOn, handleExistingModalClose, queueTryOnJob, reapplyInstructions]);

  const handleSwatchTryout = useCallback(
    async (swatchId) => {
      setSelectedSwatch(swatchId);
      setPreselectApplied(true); // user made an explicit choice; prevent auto overrides
      setCurrentStep((prev) => Math.max(prev, 2)); // move to upload step if not there
      if (!environmentFile.length) {
        setPendingSwatchId(swatchId);
        setMissingPhotoModal(true);
        return;
      }
      const job = await queueTryOnJob(swatchId);
      if (job) {
        clearPreselectedSwatch();
        setCurrentStep(4); // show final generate step
      }
    },
    [clearPreselectedSwatch, environmentFile.length, queueTryOnJob],
  );

  useEffect(() => {
    if (pendingSwatchId && environmentFile.length) {
      queueTryOnJob(pendingSwatchId).then((job) => {
        if (job) {
          clearPreselectedSwatch();
          setCurrentStep(4); // move to final generate view
          // Keep photo meta intact
        }
      });
      setPendingSwatchId(null);
      setMissingPhotoModal(false);
    }
  }, [clearPreselectedSwatch, environmentFile, pendingSwatchId, queueTryOnJob]);

  const advanceStep = (target) => {
    // guard based on current step
    const step = typeof target === 'number' ? target : currentStep + 1;
    if (step > currentStep) {
      if (currentStep === 0 && !userId.trim()) {
        message.warning('Enter a user ID to continue');
        return;
      }
      if (currentStep === 1 && selectedSwatches.length === 0) {
        message.warning('Select at least one swatch to continue');
        return;
      }
      if (currentStep === 2 && !environmentFile.length) {
        message.warning('Upload a lifestyle photo to continue');
        return;
      }
    }
    // If user is moving past swatch selection without a primary swatch, default to the first comparison swatch
    if (!selectedSwatch && selectedSwatches.length) {
      setSelectedSwatch(selectedSwatches[0]);
    }
    setCurrentStep(step);
  };

  const refreshGallery = useCallback(async () => {
    if (!normalizedUserId) {
      setGalleryItems([]);
      return;
    }
    try {
      const data = await listGallery({ user_id: normalizedUserId, page_size: 50 });
      setGalleryItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      message.error(error.message || 'Unable to load user images');
    }
  }, [normalizedUserId]);

  const loadLatestGalleryPreview = useCallback(async () => {
    if (!normalizedUserId) return null;
    try {
      const data = await listGallery({ user_id: normalizedUserId, page_size: 1 });
      const items = Array.isArray(data?.items) ? data.items : [];
      if (items.length && items[0].overlay_url) {
        setResultPreview(ensureAbsoluteUrl(items[0].overlay_url));
        return items[0].overlay_url;
      }
    } catch (error) {
      // silent fallback
    }
    return null;
  }, [normalizedUserId, ensureAbsoluteUrl]);

  const handleDeleteGalleryEntry = useCallback(
    async (galleryId) => {
      try {
        await deleteGalleryEntry(galleryId);
        message.success('Image deleted');
        refreshGallery();
      } catch (error) {
        message.error(error.message || 'Unable to delete image');
      }
    },
    [refreshGallery],
  );

  const handleDeleteAllForUser = useCallback(async () => {
    if (!normalizedUserId) {
      message.warning('Enter a user ID first');
      return;
    }
    try {
      await deleteGalleryForUser(normalizedUserId);
      message.success('All images for this user deleted');
      refreshGallery();
    } catch (error) {
      message.error(error.message || 'Unable to delete user images');
    }
  }, [normalizedUserId, refreshGallery]);

  return (
    <div className="ai-image-masking-page">
      <ModuleNav activeKey="try-v2" />

      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={galleryInputRef}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            applyEnvironmentFile(file);
          }
          event.target.value = '';
        }}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        ref={cameraInputRef}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            applyEnvironmentFile(file);
          }
          event.target.value = '';
        }}
      />

      <Card className="shadow-sm mb-3" bodyStyle={{ padding: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6}>
            <Text strong>Step summary</Text>
          </Col>
          <Col xs={24} md={18}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  User ID
                </Text>
                <Paragraph className="mb-0">{userId.trim() || 'Not set'}</Paragraph>
              </Col>
              <Col xs={24} md={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Swatch
                </Text>
                {primarySwatchData ? (
                  <Space align="start">
                    <Image
                      src={primarySwatchData.blob_url}
                      alt={primarySwatchData.name}
                      preview={false}
                      width={48}
                      height={48}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                    />
                    <div>
                      <Text strong>{primarySwatchData.name}</Text>
                      <div>
                        {(primarySwatchData.tags || []).slice(0, 2).map((t) => (
                          <Tag key={t} className="mt-1">
                            {t}
                          </Tag>
                        ))}
                        {primarySwatchData.tags?.length > 2 && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            +{primarySwatchData.tags.length - 2}
                          </Text>
                        )}
                      </div>
                    </div>
                  </Space>
                ) : (
                  <Paragraph className="mb-0">Not selected</Paragraph>
                )}
                {selectedSwatches.length > 0 && (
                  <Paragraph className="mb-0 mt-1" type="secondary" style={{ fontSize: 12 }}>
                    {selectedSwatches.length} swatch{selectedSwatches.length !== 1 ? 'es' : ''} selected
                  </Paragraph>
                )}
              </Col>
              <Col xs={24} md={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Lifestyle photo
                </Text>
                {environmentPreview ? (
                  <Image
                    src={environmentPreview}
                    alt="Lifestyle"
                    preview={false}
                    width={64}
                    height={48}
                    style={{ objectFit: 'cover', borderRadius: 8 }}
                  />
                ) : (
                  <Paragraph className="mb-0">Not uploaded</Paragraph>
                )}
              </Col>
              <Col xs={24} md={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Agent
                </Text>
                <Paragraph className="mb-0">
                  {agentTag?.trim()
                    ? `Tag: ${agentTag.trim()}`
                    : selectedSwatchData?.tags?.length
                    ? `From swatch tag: ${selectedSwatchData.tags[0]}`
                    : 'Default'}
                </Paragraph>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <Card className="shadow-sm mb-4" bodyStyle={{ padding: 16 }}>
        <Steps
          current={currentStep}
          items={[
            { title: 'User ID' },
            { title: 'Select swatch' },
            { title: 'Upload photo' },
            { title: 'Instructions & tag' },
            { title: 'Generate' },
          ]}
          responsive
        />
      </Card>

      {currentStep === 0 && (
        <Card className="shadow-sm mb-4" title="Step 1 · Enter user ID" bodyStyle={{ padding: 24 }}>
          <Space direction="vertical" size="large" className="w-100">
            <div>
              <Text strong>User ID</Text>
              <Input
                className="mt-2"
                placeholder="e.g., customer@example.com"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                size="large"
              />
            </div>
            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button type="primary" onClick={() => advanceStep(1)}>
                Next: Select swatch
              </Button>
            </div>
          </Space>
        </Card>
      )}

      {currentStep === 1 && (
        <Card className="shadow-sm mb-4" title="Step 2 · Select a swatch" bodyStyle={{ padding: 24 }}>
          <Space direction="vertical" size="large" className="w-100">
            <Search
              allowClear
              enterButton="Search"
              placeholder="Search swatches"
              onSearch={(value) => {
                setSwatchPage(1);
                setSwatchSearch(value.trim());
              }}
            />
            <List
              grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
              dataSource={swatches}
              loading={swatchLoading}
              className="mt-2"
              locale={{ emptyText: 'No swatches found' }}
              renderItem={(swatch) => (
                <List.Item key={swatch.id}>
                  <Card
                    size="small"
                    hoverable
                    style={{
                      borderColor: selectedSwatches.includes(swatch.id)
                        ? (swatch.id === selectedSwatch ? '#1677ff' : '#52c41a')
                        : undefined,
                      borderWidth: selectedSwatches.includes(swatch.id) ? 2 : 1,
                    }}
                    cover={
                      <Image
                        src={swatch.blob_url}
                        alt={swatch.name}
                        preview={false}
                        style={{ height: 120, objectFit: 'cover' }}
                      />
                    }
                    actions={[
                      <Checkbox
                        key={`select-${swatch.id}`}
                        checked={selectedSwatches.includes(swatch.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const swatchId = swatch.id;
                          
                          setSelectedSwatches((prev) => {
                            let next;
                            if (checked) {
                              // Add swatch if not already present
                              next = prev.includes(swatchId) ? prev : [...prev, swatchId];
                            } else {
                              // Remove swatch
                              next = prev.filter((id) => id !== swatchId);
                            }
                            // Max 6 swatches
                            return next.slice(0, 6);
                          });
                          
                          // Update primary swatch selection
                          if (checked) {
                            // Set as primary if no primary exists
                            setSelectedSwatch((prev) => prev || swatchId);
                          } else {
                            // If unchecking the primary, set next one as primary
                            setSelectedSwatch((prev) => {
                              if (prev === swatchId) {
                                // Find next available from current selection
                                const remaining = selectedSwatches.filter((id) => id !== swatchId);
                                return remaining.length > 0 ? remaining[0] : null;
                              }
                              return prev;
                            });
                          }
                        }}
                      >
                        Select
                      </Checkbox>,
                    ]}
                  >
                    <Card.Meta
                      title={<span style={{ fontWeight: 600 }}>{swatch.name}</span>}
                      description={<Text type="secondary">{swatch.description}</Text>}
                    />
                    <Space size="small" wrap className="mt-2">
                      {(swatch.tags || []).slice(0, 3).map((t) => (
                        <Tag key={`${swatch.id}-${t}`}>{t}</Tag>
                      ))}
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
            <Pagination
              className="mt-2"
              current={swatchPage}
              pageSize={SWATCH_PAGE_SIZE}
              total={swatchTotal}
              onChange={(page) => setSwatchPage(page)}
            />
            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button onClick={() => setCurrentStep(0)}>Back</Button>
              <Button type="primary" onClick={() => advanceStep(2)}>
                Next: Upload photo
              </Button>
            </div>
          </Space>
        </Card>
      )}

      {currentStep === 2 && (
        <Card className="shadow-sm mb-4" title="Step 3 · Upload lifestyle photo" bodyStyle={{ padding: 24 }}>
          <Space direction="vertical" size="large" className="w-100">
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} md={16}>
                <Upload.Dragger {...uploadProps} style={{ minHeight: 220 }}>
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">Drop or click to upload</p>
                  <p className="ant-upload-hint">High-resolution JPG/PNG up to 25 MB</p>
                </Upload.Dragger>
              </Col>
              <Col xs={24} md={8}>
                {primarySwatchData ? (
                  <Card
                    size="small"
                    title="Selected swatch"
                    bordered
                    style={{ height: '100%' }}
                    bodyStyle={{ padding: 12 }}
                  >
                    <Image
                      src={primarySwatchData.blob_url}
                      alt={primarySwatchData.name}
                      preview={false}
                      style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8 }}
                    />
                    <Text strong className="d-block mt-2">
                      {primarySwatchData.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Chosen from Swatches
                    </Text>
                  </Card>
                ) : (
                  <Card size="small" bordered style={{ height: '100%' }} bodyStyle={{ padding: 12 }}>
                    <Text strong>No swatch selected</Text>
                    <Paragraph type="secondary" className="mb-0" style={{ fontSize: 12 }}>
                      Pick a swatch first.
                    </Paragraph>
                  </Card>
                )}
                {comparisonSwatchData.length > 1 && (
                  <Card
                    size="small"
                    title="Comparison swatches"
                    bordered
                    className="mt-2"
                    bodyStyle={{ padding: 12 }}
                  >
                    <Space direction="vertical" size="small" className="w-100">
                      {comparisonSwatchData.map((swatch) => (
                        <Space key={`compare-list-${swatch.id}`}>
                          <Image
                            src={swatch.blob_url}
                            alt={swatch.name}
                            preview={false}
                            width={36}
                            height={36}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                          />
                          <div>
                            <Text strong>{swatch.name}</Text>
                            <div>
                              {(swatch.tags || []).slice(0, 2).map((t) => (
                                <Tag key={`${swatch.id}-tag-${t}`} className="mt-1">
                                  {t}
                                </Tag>
                              ))}
                            </div>
                          </div>
                        </Space>
                      ))}
                    </Space>
                  </Card>
                )}
              </Col>
            </Row>
            <div className="d-flex gap-2 mt-2 flex-wrap">
              <Button size="small" icon={<CameraOutlined />} onClick={() => cameraInputRef.current?.click()}>
                Use camera
              </Button>
              <Button size="small" icon={<PictureOutlined />} onClick={() => galleryInputRef.current?.click()}>
                Choose from gallery
              </Button>
            </div>
            {environmentPreview && (
              <div className="mt-3">
                <Text strong>Uploaded preview</Text>
                <Row gutter={[12, 12]} align="middle" className="mt-1">
                  <Col xs={24} md={8}>
                    <Card size="small" bordered bodyStyle={{ padding: 8 }}>
                      <Image
                        src={environmentPreview}
                        alt="Preview thumbnail"
                        style={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 8 }}
                        preview={false}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={16}>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">Filename: {photoInfo.name}</Text>
                      <Text type="secondary">Size: {photoInfo.sizeLabel}</Text>
                      <Text type="secondary">Type: {photoInfo.type}</Text>
                      <Text type="secondary">Dimensions: {photoInfo.dimensions}</Text>
                      <Text type="secondary">DPI: {photoInfo.dpi}</Text>
                    </Space>
                  </Col>
                </Row>
              </div>
            )}
            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button onClick={() => setCurrentStep(1)}>Back</Button>
              <Button type="primary" onClick={() => advanceStep(3)}>
                Next: Instructions & tag
              </Button>
            </div>
          </Space>
        </Card>
      )}

      {currentStep === 3 && (
        <Card className="shadow-sm mb-4" title="Step 4 · Additional instructions & tag" bodyStyle={{ padding: 24 }}>
          <Space direction="vertical" size="large" className="w-100">
            <div>
              <Text strong>Guidance prompt</Text>
              <TextArea
                rows={4}
                className="mt-2"
                placeholder="E.g., focus on the sofa cushions"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>
            <div>
              <Text strong>Agent tag</Text>
              <Input
                className="mt-2"
                placeholder="Enter agent tag (optional)"
                value={agentTag}
                onChange={(e) => setAgentTag(e.target.value)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Tag routes the request to a matching agent. Leave blank to use the default system prompt or swatch tag.
              </Text>
            </div>
            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button onClick={() => setCurrentStep(2)}>Back</Button>
              <Button
                type="primary"
                onClick={() => {
                  if (!userId.trim()) {
                    message.warning('Enter a user ID to continue');
                    setCurrentStep(0);
                    return;
                  }
                  if (selectedSwatches.length === 0) {
                    message.warning('Select at least one swatch to continue');
                    setCurrentStep(1);
                    return;
                  }
                  if (!environmentFile.length) {
                    message.warning('Upload a lifestyle photo to continue');
                    setCurrentStep(2);
                    return;
                  }
                  // Run for all selected swatches
                  if (selectedSwatches.length >= 2) {
                    queueComparisonJob();
                  } else {
                    handleSwatchTryout(selectedSwatches[0]);
                  }
                  setCurrentStep(4); // final step after queue
                }}
              >
                Run Tryout ({selectedSwatches.length} swatch{selectedSwatches.length !== 1 ? 'es' : ''})
              </Button>
            </div>
          </Space>
        </Card>
      )}

      {currentStep >= 3 && (
        <Card className="shadow-sm" title="Final · Generate tryout" bodyStyle={{ padding: 16 }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card
                title="Original photo"
                className="shadow-sm"
                bodyStyle={{ padding: 24, minHeight: PREVIEW_CARD_MIN_HEIGHT }}
              >
                {environmentPreview ? (
                  <Image
                    src={environmentPreview}
                    alt="Original lifestyle"
                    style={{ width: '100%', borderRadius: 16, maxHeight: 520, objectFit: 'cover' }}
                  />
                ) : (
                  <Empty description="Upload a lifestyle photo to see it here." />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title="Live Preview"
                className="shadow-sm"
                bodyStyle={{ padding: 24, minHeight: PREVIEW_CARD_MIN_HEIGHT }}
              >
                {showProcessing && (
                  <div className="mb-3 text-center">
                    <Image
                      src={PROCESSING_GIF}
                      alt="Processing"
                      preview={false}
                      style={{ width: 160, height: 120, objectFit: 'contain' }}
                    />
                    <Paragraph className="mb-1">Building your tryout...</Paragraph>
                    <Text type="secondary">You can keep browsing swatches while this finishes.</Text>
                    <Progress className="mt-2" percent={Math.round(currentProgress)} status="active" />
                  </div>
                )}
                {resultPreview ? (
                  <Image src={resultPreview} alt="Result" style={{ width: '100%', borderRadius: 16 }} />
                ) : (
                  <Empty description="Upload a lifestyle photo and pick a swatch to start a tryout." />
                )}
                {jobMeta && (
                  <>
                    <Alert
                      className="mt-3"
                      type="success"
                      showIcon
                      message={`Job ${jobMeta.job_id || 'complete'}`}
                      description={`Model ${jobMeta.model || 'custom'} · Status ${jobMeta.status}${
                        jobMeta.duration_ms ? ` · Time ${formatDuration(jobMeta.duration_ms)}` : ''
                      }`}
                    />
                    <div className="mt-2">
                      <Text type="secondary">Agent Tag: {jobMeta.agent_tag || 'default'}</Text>
                    </div>
                    <Button className="mt-3" onClick={() => setDebugVisible(true)}>
                      View AI Processing Debug
                    </Button>
                  </>
                )}
                {(debugSnapshots.length > 0 || prompt) && (
                  <div className="mt-4">
                    <Text strong>Debug snapshots (200 × 200)</Text>
                    <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                      Inputs and intermediate previews while preprocessing and generation run.
                    </Paragraph>
                    {debugSnapshots.length ? (
                      <Space size="middle" wrap>
                        {debugSnapshots.map((item) => (
                          <Card
                            key={item.key}
                            size="small"
                            bodyStyle={{ padding: 8 }}
                            style={{ width: 220 }}
                            className="shadow-sm"
                          >
                            <div
                              style={{
                                width: 200,
                                height: 200,
                                borderRadius: 12,
                                overflow: 'hidden',
                                background: '#fafafa',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Image
                                src={item.url}
                                alt={item.label}
                                width={200}
                                height={200}
                                style={{ objectFit: 'contain' }}
                                preview
                              />
                            </div>
                            <Text className="d-block mt-2">{item.label}</Text>
                          </Card>
                        ))}
                      </Space>
                    ) : (
                      <Paragraph type="secondary">Waiting for stage previews...</Paragraph>
                    )}
                    {prompt && (
                      <div className="mt-3">
                        <Text strong>Prompt sent to AI</Text>
                        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{prompt}</Paragraph>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </Col>
          </Row>
          <div className="d-flex justify-content-end gap-2 flex-wrap mt-3">
            <Button onClick={() => setCurrentStep(3)}>Back</Button>
            <Button 
              type="primary" 
              onClick={() => {
                if (selectedSwatches.length === 0) {
                  message.warning('Select at least one swatch to run tryout');
                  setCurrentStep(1);
                  return;
                }
                if (!environmentFile.length) {
                  message.warning('Upload a lifestyle photo first');
                  setCurrentStep(2);
                  return;
                }
                // Run for all selected swatches
                if (selectedSwatches.length >= 2) {
                  queueComparisonJob();
                } else {
                  handleSwatchTryout(selectedSwatches[0]);
                }
              }}
              loading={showProcessing}
              disabled={selectedSwatches.length === 0 || !environmentFile.length}
            >
              {showProcessing ? 'Processing...' : `Run Tryout (${selectedSwatches.length} swatch${selectedSwatches.length !== 1 ? 'es' : ''})`}
            </Button>
          </div>
        </Card>
      )}
      {currentStep >= 3 && selectedSwatches.length >= 2 && (
        <Card className="shadow-sm mt-3" title="Compare selected swatches" bodyStyle={{ padding: 16 }}>
          <Space direction="vertical" size="middle" className="w-100">
            <Text type="secondary">
              You have selected {selectedSwatches.length} swatches. We will apply them to the same lifestyle photo
              in the background and open a comparison drawer when ready.
            </Text>
            <Space size="small" wrap>
              {selectedSwatches.length ? (
                selectedSwatches.map((id) => (
                  <Tag key={`compare-pill-${id}`} color="blue">
                    {resolveSwatchLabel(id)}
                  </Tag>
                ))
              ) : (
                <Text type="secondary">No swatches selected for comparison yet.</Text>
              )}
            </Space>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                type="primary"
                onClick={queueComparisonJob}
                disabled={selectedSwatches.length < 2 || !environmentFile.length || !normalizedUserId}
              >
                Run background comparison
              </Button>
              <Button
                onClick={() => {
                  if (!comparisonResults.length) {
                    message.info('No comparison results yet. Queue a comparison first.');
                    return;
                  }
                  setComparisonDrawerVisible(true);
                }}
                disabled={!comparisonResults.length}
              >
                Show last comparison
              </Button>
            </div>
          </Space>
        </Card>
      )}
      <Row gutter={[24, 24]} className="mt-4">
        <Col span={24}>
          <Card title="Tryout activity" className="shadow-sm" bodyStyle={{ padding: 24 }}>
            {jobs.length === 0 ? (
              <Empty
                description={
                  normalizedUserId ? 'No tryouts queued yet.' : 'Enter a user ID to view tryout progress.'
                }
              />
            ) : (
              <List
                dataSource={jobs}
                renderItem={(job) => (
                  <List.Item
                    actions={
                      extractJobPreview(job)
                        ? [
                            <Button
                              size="small"
                              onClick={() => {
                                const preview = extractJobPreview(job);
                                if (preview) {
                                  setResultPreview(preview);
                                }
                                setJobMeta({
                                  job_id: job.job_id,
                                  status: job.status,
                                  agent_tag: job.agent_tag || 'default',
                                  duration_ms: extractJobDuration(job),
                                  model: normalizeDebugPayload(job.debug_payload)?.model,
                                });
                              }}
                              key="view"
                            >
                              View
                            </Button>,
                          ]
                        : []
                    }
                  >
                    <List.Item.Meta
                      title={
                        <div className="d-flex justify-content-between flex-wrap gap-2">
                          <Text strong>{job.status?.toUpperCase() || 'PENDING'}</Text>
                          <Text type="secondary">
                            {job.created_at ? new Date(job.created_at).toLocaleString() : ''}
                          </Text>
                        </div>
                      }
                      description={
                        <Space direction="vertical" size={4} className="w-100">
                          <Text type="secondary">Swatch #{job.swatch_id}</Text>
                          <Progress
                            percent={Math.round(job.progress ?? (job.status === 'completed' ? 100 : 0))}
                            size="small"
                            status={job.status === 'failed' ? 'exception' : 'active'}
                          />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {job.message || 'Processing'}
                          </Text>
                          {extractJobDuration(job) != null && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Time: {formatDuration(extractJobDuration(job))}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mt-4">
        <Col span={24}>
          <Card
            title="User images"
            className="shadow-sm"
            bodyStyle={{ padding: 24 }}
            extra={
              <Popconfirm
                title="Delete all images for this user?"
                okText="Delete all"
                okButtonProps={{ danger: true }}
                onConfirm={handleDeleteAllForUser}
                disabled={!normalizedUserId}
              >
                <Button danger size="small" disabled={!normalizedUserId}>
                  Delete all for user
                </Button>
              </Popconfirm>
            }
          >
            {galleryItems.length === 0 ? (
              <Empty
                description={
                  normalizedUserId ? 'No images saved for this user.' : 'Enter a user ID to view saved images.'
                }
              />
            ) : (
              <List
                loading={galleryLoading}
                grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
                dataSource={galleryItems}
                renderItem={(item) => (
                  <List.Item key={item.id}>
                    <Card
                      size="small"
                      cover={
                        item.overlay_url ? (
                          <Image
                            src={item.overlay_url}
                            alt={item.swatch_name}
                            style={{ height: 180, objectFit: 'cover' }}
                            preview={false}
                          />
                        ) : (
                          <div
                            style={{
                              height: 180,
                              background: '#fafafa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999',
                            }}
                          >
                            No preview
                          </div>
                        )
                      }
                      actions={[
                        <Popconfirm
                          key="delete"
                          title="Delete this image?"
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleDeleteGalleryEntry(item.id)}
                        >
                          <Button danger size="small">Delete</Button>
                        </Popconfirm>,
                      ]}
                    >
                      <Card.Meta
                        title={item.swatch_name || `Swatch #${item.swatch_id}`}
                        description={
                          <div>
                            <div style={{ fontSize: 12, color: '#888' }}>{item.swatch_description || 'No description'}</div>
                            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                              Updated: {item.updated_at ? new Date(item.updated_at).toLocaleString() : '—'}
                            </div>
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mt-4">
        <Col span={24}>
          <Card title="Comparison jobs" className="shadow-sm" bodyStyle={{ padding: 24 }}>
            {batchJobs.length === 0 ? (
              <Empty
                description={
                  normalizedUserId
                    ? 'No comparison jobs queued yet.'
                    : 'Enter a user ID to view comparison progress.'
                }
              />
            ) : (
              <List
                dataSource={batchJobs}
                renderItem={(job) => (
                  <List.Item
                    actions={
                      job.results && job.results.length
                        ? [
                            <Button
                              size="small"
                              onClick={() => {
                                setComparisonResults(normalizeBatchResults(job.results));
                                setComparisonDrawerVisible(true);
                              }}
                              key={`view-batch-${job.job_id}`}
                            >
                              View
                            </Button>,
                          ]
                        : []
                    }
                  >
                    <List.Item.Meta
                      title={
                        <div className="d-flex justify-content-between flex-wrap gap-2">
                          <Text strong>{job.status?.toUpperCase() || 'PENDING'}</Text>
                          <Text type="secondary">
                            {job.created_at ? new Date(job.created_at).toLocaleString() : ''}
                          </Text>
                        </div>
                      }
                      description={
                        <Space direction="vertical" size={4} className="w-100">
                          <Text type="secondary">
                            Swatches: {(job.swatch_ids || []).join(', ') || '—'}
                          </Text>
                          <Progress
                            percent={Math.round(job.progress ?? (job.status === 'completed' ? 100 : 0))}
                            size="small"
                            status={job.status === 'failed' ? 'exception' : 'active'}
                          />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {job.message || 'Processing'}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mt-4">
        <Col span={24}>
          <Card className="shadow-sm" bodyStyle={{ padding: 24 }}>
            <Title level={4}>Pipeline</Title>
            <Timeline
              items={[
                { color: 'blue', children: 'Upload lifestyle photo & capture context' },
                { color: 'green', children: 'Select swatch & optional agent tag' },
                { color: 'purple', children: 'Stream overlay generation (OpenAI or fallback)' },
                { color: 'gray', children: 'Persist results to the studio library' },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Drawer
        open={comparisonDrawerVisible}
        onClose={() => setComparisonDrawerVisible(false)}
        placement="right"
        width={`${comparisonDrawerWidth}vw`}
        title="Comparison preview"
      >
        <Space direction="vertical" size="middle" className="w-100">
          <div>
            <Text strong>Resize preview width</Text>
            <Slider min={40} max={75} value={comparisonDrawerWidth} onChange={(value) => setComparisonDrawerWidth(value)} />
          </div>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card size="small" title="Original lifestyle">
                {environmentPreview ? (
                  <Image
                    src={environmentPreview}
                    alt="Original lifestyle"
                    style={{ width: '100%', borderRadius: 12, objectFit: 'cover' }}
                  />
                ) : (
                  <Empty description="Upload a lifestyle photo to compare." />
                )}
              </Card>
            </Col>
            {displayComparisonResults.length === 0 && (
              <Col span={24}>
                <Empty description="No comparison results yet. Queue a comparison to see side-by-side overlays." />
              </Col>
            )}
            {displayComparisonResults.map((result) => {
              const swatchMeta = resolveSwatchMeta(result.swatch_id, result.swatch_name);
              return (
                <Col xs={24} sm={12} key={`compare-${result.swatch_id}-${result.status}`}>
                  <Card
                    size="small"
                    title={resolveSwatchLabel(result.swatch_id, result.swatch_name)}
                    extra={<Tag>{(result.status || 'pending').toUpperCase()}</Tag>}
                    bodyStyle={{ minHeight: 220 }}
                  >
                    {result.overlay_url ? (
                      <Image
                        src={result.overlay_url}
                        alt={resolveSwatchLabel(result.swatch_id, result.swatch_name)}
                        style={{ width: '100%', borderRadius: 12, objectFit: 'cover' }}
                      />
                    ) : (
                      <Empty description={result.message || 'Overlay unavailable'} />
                    )}
                    <Paragraph className="mb-0 mt-2" type="secondary">
                      {result.message || (result.status === 'completed' ? 'Completed' : 'Processing')}
                    </Paragraph>
                    <Space
                      align="center"
                      className="mt-2"
                      style={{ width: '100%', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}
                    >
                      {swatchMeta.blob_url ? (
                        <Image
                          src={swatchMeta.blob_url}
                          alt={swatchMeta.name}
                          preview={false}
                          width={40}
                          height={40}
                          style={{ objectFit: 'cover', borderRadius: 6 }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 6,
                            background: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            color: '#999',
                          }}
                        >
                          —
                        </div>
                      )}
                      <div style={{ lineHeight: 1.4 }}>
                        <Text strong>{swatchMeta.name}</Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {swatchMeta.description || 'No description'}
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Space>
      </Drawer>

      <Modal
        width={720}
        open={debugVisible}
        title="AI Request Debug"
        onCancel={() => setDebugVisible(false)}
        footer={null}
      >
        {debugDetails ? (
          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            <Paragraph>
              <strong>Engine:</strong> {debugPayload?.engine || debugDetails.engine || 'N/A'}
            </Paragraph>
            <Paragraph>
              <strong>Model:</strong> {debugPayload?.model || 'N/A'}
            </Paragraph>
            {debugError && (
              <Paragraph type="warning">
                <strong>Error:</strong> {debugError}
              </Paragraph>
            )}
            {debugErrorType && (
              <Paragraph type="secondary">
                <strong>Error Type:</strong> {debugErrorType}
              </Paragraph>
            )}
            {debugPayload?.system_prompt && (
              <>
                <Paragraph>
                  <strong>System Prompt</strong>
                </Paragraph>
                <pre>{debugPayload.system_prompt}</pre>
              </>
            )}
            {debugPayload?.composed_prompt && (
              <>
                <Paragraph>
                  <strong>Composed Prompt Sent To OpenAI</strong>
                </Paragraph>
                <pre>{debugPayload.composed_prompt}</pre>
              </>
            )}
            <Paragraph>
              <strong>Method:</strong> {requestMethod}
            </Paragraph>
            <Paragraph>
              <strong>URL:</strong> {requestUrl}
            </Paragraph>
            <Paragraph>
              <strong>Headers</strong>
            </Paragraph>
            <pre>{JSON.stringify(requestHeaders, null, 2)}</pre>
            <Paragraph>
              <strong>Request Body</strong>
            </Paragraph>
            <pre>{JSON.stringify(requestFields, null, 2)}</pre>
            <Paragraph>
              <strong>Files</strong>
            </Paragraph>
            <pre>{JSON.stringify(requestFiles, null, 2)}</pre>
            {responseDetails && (
              <>
                <Paragraph className="mt-3">
                  <strong>Response</strong>
                </Paragraph>
                <Paragraph>
                  <strong>Status:</strong> {responseDetails.status_code ?? responseDetails.status ?? 'N/A'}
                </Paragraph>
                {(responseDetails.error || responseDetails.error_type) && (
                  <>
                    <Paragraph>
                      <strong>Engine Error</strong>
                    </Paragraph>
                    <pre>
                      {JSON.stringify(
                        {
                          error_type: responseDetails.error_type || null,
                          error: responseDetails.error || null,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </>
                )}
                <Paragraph>
                  <strong>Response Body</strong>
                </Paragraph>
                <pre>{JSON.stringify(responseDetails, null, 2)}</pre>
                {responseDetails.headers && (
                  <>
                    <Paragraph>
                      <strong>Response Headers</strong>
                    </Paragraph>
                    <pre>{JSON.stringify(responseDetails.headers, null, 2)}</pre>
                  </>
                )}
                {responseDetails.json && (
                  <>
                    <Paragraph>
                      <strong>Response JSON</strong>
                    </Paragraph>
                    <pre>{JSON.stringify(responseDetails.json, null, 2)}</pre>
                  </>
                )}
                {responseDetails.text && (
                  <>
                    <Paragraph>
                      <strong>Response Text</strong>
                    </Paragraph>
                    <pre>{responseDetails.text}</pre>
                  </>
                )}
              </>
            )}
            {failedAttempts.length > 0 && (
              <>
                <Paragraph className="mt-3">
                  <strong>Failed Attempts ({failedAttempts.length})</strong>
                </Paragraph>
                {failedAttempts.map((attempt, idx) => (
                  <div key={`failed-attempt-${idx}`} style={{ marginBottom: 16 }}>
                    <Paragraph className="mb-1">
                      <strong>
                        #{idx + 1} {attempt?.engine_path || 'engine'} · {attempt?.model || 'unknown model'}
                      </strong>
                    </Paragraph>
                    <Paragraph className="mb-1">Request</Paragraph>
                    <pre>{JSON.stringify(attempt?.request || {}, null, 2)}</pre>
                    <Paragraph className="mb-1">Response</Paragraph>
                    <pre>{JSON.stringify(attempt?.response || {}, null, 2)}</pre>
                  </div>
                ))}
              </>
            )}
            {!hasDebugTransportData && (
              <Alert
                className="mt-3"
                type="info"
                showIcon
                message="No request metadata captured for this run."
                description="This can happen when a fallback path completes without upstream request/response debug capture."
              />
            )}
          </div>
        ) : (
          <Paragraph type="secondary">
            No AI request details were returned for this job (fallback overlay used).
          </Paragraph>
        )}
      </Modal>

      <Modal
        width={900}
        open={existingModalVisible}
        title="Existing tryout found"
        onCancel={handleExistingModalClose}
        footer={null}
      >
        {existingTryOn ? (
          <Space direction="vertical" size="large" className="w-100">
            <Alert
              type="info"
              showIcon
              message="This lifestyle photo has already been processed with the selected swatch."
              description="Reuse the previous overlay or reapply with new guidance."
            />
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card size="small" title="Original lifestyle">
                  {existingTryOn.original_url ? (
                    <Image
                      src={existingTryOn.original_url}
                      alt="Original lifestyle"
                      style={{ width: '100%', borderRadius: 12 }}
                    />
                  ) : (
                    <Empty description="Original image unavailable" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Existing overlay">
                  {existingTryOn.overlay_url ? (
                    <Image
                      src={existingTryOn.overlay_url}
                      alt="Existing overlay"
                      style={{ width: '100%', borderRadius: 12 }}
                    />
                  ) : (
                    <Empty description="Overlay not found" />
                  )}
                </Card>
              </Col>
            </Row>
            <div>
              <Text strong>Additional instructions (optional)</Text>
              <TextArea
                rows={3}
                className="mt-2"
                placeholder="Add any extra guidance to append to the system prompt before reapplying."
                value={reapplyInstructions}
                onChange={(e) => setReapplyInstructions(e.target.value)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                These instructions are appended to the system prompt so OpenAI gets your extra context.
              </Text>
            </div>
            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button onClick={handleUseExistingResult}>Use existing result</Button>
              <Button type="primary" loading={reapplyLoading} onClick={handleReapplyWithInstructions}>
                Reapply overlay
              </Button>
            </div>
          </Space>
        ) : (
          <Skeleton active paragraph={{ rows: 6 }} />
        )}
      </Modal>

      <Modal
        open={missingPhotoModal}
        title="Add a lifestyle photo"
        onCancel={() => {
          setMissingPhotoModal(false);
          setPendingSwatchId(null);
        }}
        footer={null}
      >
        <Paragraph>
          A lifestyle photo is required before we can apply the swatch. Choose how you want to provide the photo.
        </Paragraph>
        <Space direction="vertical" className="w-100">
          <Button
            type="primary"
            icon={<CameraOutlined />}
            block
            onClick={() => {
              setMissingPhotoModal(false);
              cameraInputRef.current?.click();
            }}
          >
            Take photo with camera
          </Button>
          <Button
            icon={<PictureOutlined />}
            block
            onClick={() => {
              setMissingPhotoModal(false);
              galleryInputRef.current?.click();
            }}
          >
            Choose from gallery
          </Button>
        </Space>
      </Modal>
    </div>
  );
};

export default TryBeforeYouBuyV2;

